import { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";
import SideDrawerOverlay from "../SideOverLay";
import styles from "./index.module.scss";
import UserCard from "../SeatApprovalFlow/SeekerDetailsOverlay/Common/UserCards";
import GrayLine from "../../common/components/GrayLine";
import { getCall } from "../../services/apiService";
import { endPoints, PORTAL } from "../../constants/urlConstants";
import Loader from "../../common/components/Loader";
import { formatDateWithoutTime } from "../../utils/commonFunctions";
import { RootState } from "../../store";
import { QUICK_VIEW_OVERLAY, LABEL_ANY_HDB_MSD } from "../../constants/textConstants";
import { SeekerData, QuickViewOverlayProps, QuickViewHeader, FormattedValue } from "../../types/registration";
const QuickViewOverlay: React.FC<QuickViewOverlayProps> = ({
  open,
  onClose,
  seekersData,
}) => {
  const [quickViewHeaders, setQuickViewHeaders] = useState<QuickViewHeader[]>([]);
  const [detailedSeekerData, setDetailedSeekerData] = useState<SeekerData | null>(
    seekersData || null
  );
  const [loading, setLoading] = useState(false);
  const [apiSuccess, setApiSuccess] = useState(false);

  const selectedKpiOption = useSelector(
    (state: RootState) => state.ProgramReducer.selectedKpiOption
  );
  const selectedKpiTab = useSelector(
    (state: RootState) => state.ProgramReducer.selectedKpiTab
  );

  const isEmpty = (value: unknown): boolean => {
    return value === null || value === undefined || value === "";
  };

  const shouldShowBlessedDate = (seekerData?: SeekerData | null): boolean => {
    if (!seekerData) return false;
    
    const status = seekerData.approvalStatus;
    
    return typeof status === 'string' && status.toLowerCase().trim() === 'approved';
  };

  const formatValue = useCallback((
    value: unknown, 
    type: string, 
    fieldKey?: string
  ): string => {
    if (fieldKey === "approvalStatus" && detailedSeekerData) {
      const { blessedWith, approvalStatus } = detailedSeekerData;
      
      if (isEmpty(blessedWith)) {
        const mappedStatus = QUICK_VIEW_OVERLAY.STATUS_MAPPING.approvalStatus[approvalStatus as keyof typeof QUICK_VIEW_OVERLAY.STATUS_MAPPING.approvalStatus];
        return mappedStatus || (approvalStatus as string) || "-";
      }
      return blessedWith as string;
    }

    if (isEmpty(value)) return "-";

    switch (type) {
      case "date":
        return formatDateWithoutTime(value as string);
      
      case "array":
        if (Array.isArray(value)) {
          if (value.length === 0) return LABEL_ANY_HDB_MSD;
          
          return value
            .map((item) => {
              if (typeof item === "object" && item !== null) {
                const obj = item as Record<string, unknown>;
                return (obj.preferredProgram as { name?: string })?.name || 
                       obj.name || obj.label || obj.value || String(item);
              }
              return String(item);
            })
            .join(", ");
        }
        return String(value);
      
      case "number":
        return typeof value === "number" ? String(value) : String(value) || "-";
      
      default:
        return String(value);
    }
  }, [detailedSeekerData]);

  const formatGroupedValue = useCallback((
    primaryValue: unknown, 
    secondaryValue: unknown,
    primaryKey?: string,
    secondaryKey?: string
  ): FormattedValue => {
    const formattedPrimary = formatValue(primaryValue, "string");
    const formattedSecondary = formatValue(secondaryValue, "string");

    const hasValidPrimary = formattedPrimary !== "-";
    const hasValidSecondary = formattedSecondary !== "-";

    if (primaryKey === "averageRating" && secondaryKey === "rmComments") {
      if (!hasValidSecondary) {
        return { display: "-", hasLineBreak: false };
      }
      if (hasValidPrimary && hasValidSecondary) {
        return { 
          display: { primary: formattedPrimary, secondary: formattedSecondary }, 
          hasLineBreak: true 
        };
      }
      return { display: formattedSecondary, hasLineBreak: false };
    }

    if (!hasValidPrimary && !hasValidSecondary) {
      return { display: "-", hasLineBreak: false };
    }

    if (!hasValidPrimary) {
      return { display: formattedSecondary, hasLineBreak: false };
    }

    if (!hasValidSecondary) {
      return { display: formattedPrimary, hasLineBreak: false };
    }

    return { 
      display: { primary: formattedPrimary, secondary: formattedSecondary }, 
      hasLineBreak: true 
    };
  }, [formatValue]);

  const buildApiUrl = useCallback((): string => {
    if (!seekersData?.id || !seekersData?.programId) return "";

    let url = `${endPoints.registeredSeekerList}?limit=${QUICK_VIEW_OVERLAY.API_CONFIG.LIMIT}&offset=${QUICK_VIEW_OVERLAY.API_CONFIG.OFFSET}&programId=${seekersData.programId}`;

    if (selectedKpiOption?.value) {
      url += `&parentFilter=${selectedKpiOption.value}`;
    }

    if (selectedKpiTab) {
      const filtersObj = {
        kpiCategory: selectedKpiTab.kpiCategory,
        kpiFilter: selectedKpiTab.kpiFilter,
      };
      url += `&filters=${encodeURIComponent(JSON.stringify(filtersObj))}`;
    }

    return url;
  }, [seekersData, selectedKpiOption, selectedKpiTab]);

  const fetchSeekerData = useCallback(async () => {
    const url = buildApiUrl();
    if (!url) return;

    setLoading(true);
    setApiSuccess(false);

    try {
      const response = await getCall(url, undefined, PORTAL);
      const responseData = response?.data?.data;

      if (responseData) {
        setQuickViewHeaders(responseData.quickViewHeaders || []);

        const seekerData = Array.isArray(responseData.data)
          ? responseData.data.find((item: SeekerData) => item.id === seekersData?.id) || responseData.data
          : responseData.data || seekersData;

        setDetailedSeekerData(seekerData);
      }
    } catch (error) {
      setDetailedSeekerData(seekersData || null);
    } finally {
      setLoading(false);
      setApiSuccess(true);
    }
  }, [buildApiUrl, seekersData]);

  const processHeaders = useMemo((): QuickViewHeader[] => {
    const processedHeaders: QuickViewHeader[] = [];
    const processedKeys = new Set<string>();

    const sortedHeaders = quickViewHeaders
      .sort((a, b) => parseInt(a.order || "0") - parseInt(b.order || "0"))
      .filter((header) => !QUICK_VIEW_OVERLAY.EXCLUDED_FIELDS.includes(header.key as any))
      .filter((header) => {
        if (header.key === "blessedDate") {
          return shouldShowBlessedDate(detailedSeekerData);
        }
        return true;
      });

    for (const header of sortedHeaders) {
      if (processedKeys.has(header.key)) continue;

      const groupConfig = Object.values(QUICK_VIEW_OVERLAY.GROUPED_FIELDS).find(
        (config) =>
          config.primaryKey === header.key || config.secondaryKey === header.key
      );

      if (groupConfig) {
        if ((groupConfig.primaryKey === "blessedDate" || groupConfig.secondaryKey === "blessedDate")) {
          if (!shouldShowBlessedDate(detailedSeekerData)) {
            processedKeys.add(groupConfig.primaryKey);
            processedKeys.add(groupConfig.secondaryKey);
            continue;
          }
        }
        
        const primaryHeader = sortedHeaders.find(
          (h) => h.key === groupConfig.primaryKey
        );
        const secondaryHeader = sortedHeaders.find(
          (h) => h.key === groupConfig.secondaryKey
        );

        if (primaryHeader || secondaryHeader) {
          processedHeaders.push({
            key: groupConfig.groupedKey,
            label: groupConfig.label,
            type: "grouped",
            order: primaryHeader?.order || secondaryHeader?.order,
            primaryKey: groupConfig.primaryKey,
            secondaryKey: groupConfig.secondaryKey,
          });

          processedKeys.add(groupConfig.primaryKey);
          processedKeys.add(groupConfig.secondaryKey);
        }
      } else {
        processedHeaders.push(header);
        processedKeys.add(header.key);
      }
    }

    return processedHeaders;
  }, [quickViewHeaders, detailedSeekerData]);

  const { gridHeaders, singleColumnHeaders } = useMemo(() => ({
    gridHeaders: processHeaders.filter(
      (header) => !QUICK_VIEW_OVERLAY.SINGLE_COLUMN_FIELDS.includes(header.key as any)
    ),
    singleColumnHeaders: processHeaders.filter((header) =>
      QUICK_VIEW_OVERLAY.SINGLE_COLUMN_FIELDS.includes(header.key as any)
    ),
  }), [processHeaders]);

  useEffect(() => {
    if (open && seekersData) {
      setDetailedSeekerData(seekersData);
      fetchSeekerData();
    }
  }, [open, seekersData, fetchSeekerData]);

  const handleClose = useCallback(() => {
    setQuickViewHeaders([]);
    setDetailedSeekerData(null);
    setApiSuccess(false);
    onClose();
  }, [onClose]);

  const renderValue = (value: string | FormattedValue) => {
    if (typeof value === 'object' && value.hasLineBreak && typeof value.display === 'object') {
      return (
        <>
          <p>{value.display.primary}</p>
          <p>{value.display.secondary}</p>
        </>
      );
    }
    return typeof value === 'object' ? value.display : value;
  };

  const renderSingleColumnValue = (value: string | FormattedValue, header: QuickViewHeader) => {
    if (typeof value === 'object' && value.hasLineBreak && typeof value.display === 'object') {
      if (header.primaryKey === "averageRating") {
        return <div>{value.display.secondary}</div>;
      }
      return (
        <>
          <div>{value.display.primary}</div>
          <div className={styles.recommendationGap}>{value.display.secondary}</div>
        </>
      );
    }
    return typeof value === 'object' ? value.display : value;
  };

  const renderGridItem = (header: QuickViewHeader) => {
    if (!detailedSeekerData) return null;

    const value = header.type === "grouped" && header.primaryKey && header.secondaryKey
      ? formatGroupedValue(
          detailedSeekerData[header.primaryKey],
          detailedSeekerData[header.secondaryKey],
          header.primaryKey,
          header.secondaryKey
        )
      : formatValue(detailedSeekerData[header.key], header.type || "string", header.key);

    return (
      <div key={header.key} className={styles.detailItem}>
        <span className={styles.detailLabel}>{header.label}</span>
        <span className={styles.detailValue}>
          {renderValue(value)}
        </span>
      </div>
    );
  };

  const renderSingleColumnItem = (header: QuickViewHeader, index: number) => {
    if (!detailedSeekerData) return null;

    const value = header.type === "grouped" && header.primaryKey && header.secondaryKey
      ? formatGroupedValue(
          detailedSeekerData[header.primaryKey],
          detailedSeekerData[header.secondaryKey],
          header.primaryKey,
          header.secondaryKey
        )
      : formatValue(detailedSeekerData[header.key], header.type || "string", header.key);

    return (
      <div 
        key={header.key} 
        className={`${styles.singleColumnItem} ${index > 0 ? styles.withTopMargin : ''}`}
      >
        <span className={styles.detailLabel}>
          {header.label}
          {header.type === "grouped" && header.primaryKey === "averageRating" && (
            <span className={styles.ratingValue}>
              {formatValue(detailedSeekerData[header.primaryKey], "string")}
            </span>
          )}
        </span>
        <span className={`${styles.detailValue} ${styles.singleColumnDetailValue}`}>
          {renderSingleColumnValue(value, header)}
        </span>
      </div>
    );
  };

  if (!open) return null;

  if (loading || !apiSuccess) {
    return (
      <SideDrawerOverlay
        open={open}
        grayLine={true}
        onClose={handleClose}
        headerText={QUICK_VIEW_OVERLAY.QUICK_VIEW_HEADING}
      >
        <div className={styles.quickViewForm}>
          <div className={styles.loadingContainer}>
            <Loader type="small" />
          </div>
        </div>
      </SideDrawerOverlay>
    );
  }

  const hasValidSeekerData = seekersData && 
    typeof seekersData === "object" && 
    Object.keys(seekersData).length > 0;

  const hasProcessedHeaders = processHeaders.length > 0 && detailedSeekerData;

  return (
    <SideDrawerOverlay
      open={open}
      grayLine={true}
      onClose={handleClose}
      headerText={QUICK_VIEW_OVERLAY.QUICK_VIEW_HEADING}
    >
      <div className={styles.quickViewForm}>
        {hasValidSeekerData && (
          <UserCard seeker={seekersData} disablePreferences={true} />
        )}

        <div className={styles.filterlabelName}>
          {QUICK_VIEW_OVERLAY.DETAILS_HEADING} <GrayLine />
        </div>

        {hasProcessedHeaders ? (
          <>
            {gridHeaders.length > 0 && (
              <div className={styles.detailsGrid}>
                {gridHeaders.map(renderGridItem)}
              </div>
            )}

            {singleColumnHeaders.length > 0 && (
              <div className={styles.singleColumnContainer}>
                {singleColumnHeaders.map(renderSingleColumnItem)}
              </div>
            )}
          </>
        ) : (
          <div className={styles.noDataMessage}>
            {QUICK_VIEW_OVERLAY.NO_DATA_MESSAGE}
          </div>
        )}
      </div>
    </SideDrawerOverlay>
  );
};

export default QuickViewOverlay;